import { Component, Element, Host, Prop, State, h } from '@stencil/core';

import { onDealerAppointmentRequested, onDealerScheduleRequested, onSendDealerOffer } from '../../../actions/actions.dealer';
import { AllocatedDealers, DealerAppointmentInfo, DealerInfo, DealerOffer, Schedule } from '../../../interfaces/interface.dealer';
import { trackAppointmentRequestClick, trackSeeMoreDealersClick } from '../../../tracking/tracking.dealer';
import { mapDealerInfoToMarker } from '../../../utils/utils.dealer';
import { findClosestDealer } from '../../../utils/utils.dealer-list';

@Component({
  tag: 'dealer-list',
  styleUrl: 'dealer-list.css',
})
export class DealerList {
  @Element() el: HTMLElement;

  @Prop() shouldDisplayDealerListInfo: boolean = false;
  @Prop() expirationDate: string = '';
  @Prop() allocatedDealers: AllocatedDealers[] = [];
  @Prop() dealerOffers: Record<number, DealerOffer> = {};
  @Prop() buyingCenters: DealerInfo[] = [];
  @Prop() dealerAppointmentInfo: DealerAppointmentInfo | null = null;
  @Prop() offerId: string = '';
  @Prop() dealerName: string = 'this dealer';
  @Prop() showDealerList: boolean = false;
  @Prop() isAdmin: boolean = false;
  @Prop() showDealerChosenReasons: boolean = true;

  @State() dealerSchedule: Schedule[] = [];
  @State() shouldDisplayBuyingCenters: boolean = false;

  // track which dealer id is in appointment scope. may revisit this later...
  @State() activeDealerId: number = 0;

  onShowMoreDealersClick = () => {
    trackSeeMoreDealersClick();
    this.shouldDisplayBuyingCenters = !this.shouldDisplayBuyingCenters;
  };

  componentWillUpdate() {
    // dealer schedules comes  in via side effect
    if (this.activeDealerId in this.dealerOffers) {
      this.dealerSchedule = this.dealerOffers[this.activeDealerId].schedule;
    }
  }

  hasMoreDealers() {
    return this.buyingCenters.length > 0;
  }

  isOfferSentToDealer(dealerId) {
    if (this.dealerOffers[dealerId]) {
      return this.dealerOffers[dealerId].hasOffer || false;
    }
  }

  onOfferSendClick(dealerId) {
    onSendDealerOffer(dealerId, this.offerId);
  }

  onDealerScheduleClick(dealerId, index = 0) {
    trackAppointmentRequestClick(index);
    onDealerScheduleRequested(dealerId);
    this.activeDealerId = dealerId;

    const modal = this.el.querySelector('ico-modal#appointment') as HTMLIcoModalElement;
    modal.open();
  }

  // date/time callback from calendar
  onAppointmentTimeSelected(dayMonthYear, timeFrame) {
    onDealerAppointmentRequested({ offerId: this.offerId, dealerId: this.activeDealerId, ...dayMonthYear, timeFrame });
  }

  onModalClose() {
    this.activeDealerId = 0;
  }

  renderModal() {
    const apptRequestedDealer = this.dealerAppointmentInfo;

    const shouldRenderAppointmentRequestSentMessage = this.isOfferSentToDealer(this.activeDealerId) && apptRequestedDealer && apptRequestedDealer.dealerId === this.activeDealerId;

    if (shouldRenderAppointmentRequestSentMessage) {
      return (
        <appointment-request-sent-message
          slot="raw"
          appointmentDay={apptRequestedDealer.appointmentDay}
          appointmentMonth={apptRequestedDealer.appointmentMonth}
          appointmentYear={apptRequestedDealer.appointmentYear}
          appointmentTime={apptRequestedDealer.appointmentTime}
          dealer={apptRequestedDealer.dealershipName}
        />
      );
    }
    return (
      <appointment-request
        slot="raw"
        dealerId={this.activeDealerId}
        expirationDate={this.expirationDate}
        schedule={this.dealerSchedule}
        whenRequested={(timeFrame, dayOfWeek) => {
          this.onAppointmentTimeSelected(dayOfWeek, timeFrame);
        }}
      />
    );
  }

  dealerCount() {
    return this.allocatedDealers.length + this.buyingCenters.length;
  }

  scrollTo(index) {
    if (!this.shouldDisplayBuyingCenters && index >= this.allocatedDealers.length) {
      this.shouldDisplayBuyingCenters = !this.shouldDisplayBuyingCenters;
      setTimeout(() => {
        this.scroltoView(index);
      }, 0);
      return;
    }

    this.scroltoView(index);
  }

  private scroltoView(index: number) {
    const element = this.el.querySelector(`#dealer-item-${index + 1}`);
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  render() {
    const { shouldDisplayDealerListInfo } = this;
    if (!shouldDisplayDealerListInfo) {
      return;
    }
    const hasMultipleDealers = this.dealerCount() > 1;
    const hasNoDealers = this.dealerCount() < 1;
    const dealerListClass = {
      '@xs:px-6 @md:px-12 mb-7 pb-2 bg-clrs-white rounded-lg shadow-drop text-clrs-black': true,
      'visibility: hidden': !this.showDealerList && this.isAdmin,
    };

    const dealerMakers = this.allocatedDealers.filter((allocatedDealer) => allocatedDealer.dealerInfo).map((allocatedDealer) => mapDealerInfoToMarker(allocatedDealer.dealerInfo));
    const secoundDealerMakers = this.buyingCenters.map((dealerInfo) => mapDealerInfoToMarker(dealerInfo));
    return (
      <Host>
        <div class="@container">
          <div class={dealerListClass}>
            {hasNoDealers && (
              <div>
                <div class="text-base text-left pt-2 mr-8 mb-4">
                  We were unable locate a Kelley Blue Book Instant Cash Offer Dealer within 200 miles of your area. For help locating the nearest Kelley Blue Book Instant Cash
                  Offer Dealer at which you can redeem your offer, please contact our Customer Service at 866-559-5268.
                </div>
              </div>
            )}
            {!hasMultipleDealers && !hasNoDealers && (
              <div class="text-left pt-2 mr-8">
                <h2 class="font-montserrat text-lg mt-2 mb-1">This Dealer is Ready to Buy Your Car</h2>
                <div class="text-base mb-4">This dealer may contact you about your offer.</div>
              </div>
            )}
            {hasMultipleDealers && (
              <div>
                <div class="pt-2 mr-8">
                  <h2 class="font-montserrat text-left text-lg mt-2 mb-1">These Dealers are Ready To Buy Your Car</h2>
                  <div class="text-base text-left mb-4">Dealers may contact you about your Offer.</div>
                  {this.showDealerChosenReasons && (
                    <div>
                      <h3 class="text-left mb-1 text-base font-bold">We chose these dealers based on</h3>
                      <div class="flex ml-0 mb-4 gap-4 @xs:flex-col @4xl:flex-row justify-start">
                        <div class="@lg:flex flex-col @lg:flex-row items-start @lg:items-center justify-start @lg:justify-center text-left @lg:text-center">
                          <div class="flex items-center">
                            <svg-icon icon="your-vehicle-of-interest" width="23px" height="23px"></svg-icon>
                            <h5 class="ml-2 font-medium">Your Vehicle of Interest</h5>
                          </div>
                        </div>
                        <div class="@lg:flex flex-col @lg:flex-row items-start @lg:items-center justify-start @lg:justify-center text-left @lg:text-center @4xl:mx-5">
                          <div class="flex items-center">
                            <svg-icon icon="your-location" width="23px" height="23px"></svg-icon>
                            <h5 class="ml-2 font-medium">Your Location</h5>
                          </div>
                        </div>
                        <div class="@lg:flex flex-col @lg:flex-row items-start @lg:items-center justify-start @lg:justify-center text-left @lg:text-center">
                          <div class="flex items-center">
                            <svg-icon icon="your-trade-in" width="23px" height="23px"></svg-icon>
                            <h5 class="ml-2 font-medium">Your Trade-in</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!hasNoDealers && (
              <div>
                <google-map dealerMarkers={dealerMakers} secondaryDealerMarkers={secoundDealerMakers} whenClicked={(index) => this.scrollTo(index)} />
              </div>
            )}

            {/* slot for replacement vehicle */}
            <slot name="head" />

            <div class="dealer-list dealer-list--allocated">
              {findClosestDealer(this.allocatedDealers).map(({ dealerInfo, dealerInventory }, index) => {
                return (
                  <dealer-listing-enhancement
                    id={`dealer-item-${index + 1}`}
                    dealerInfo={dealerInfo}
                    dealerInventory={dealerInventory}
                    index={index}
                    isClosest={dealerInfo.isClosest}
                    showDealerList={this.showDealerList}>
                    {dealerInfo.acceptAppointmentRequests && (
                      <offer-appointment
                        slot="right"
                        appointmentMonth={dealerInfo.appointmentMonth}
                        appointmentTime={dealerInfo.appointmentTime}
                        appointmentYear={dealerInfo.appointmentYear}
                        appointmentDay={dealerInfo.appointmentDay}
                        buttonStyle="secondary"
                        dayOfWeek={dealerInfo.dayOfWeek}
                        timeFrame={dealerInfo.timeFrame}
                        isRequestedAppointment={dealerInfo.isRequestedAppointment}
                        whenClicked={() => {
                          this.onDealerScheduleClick(dealerInfo.dealerId, index);
                        }}
                      />
                    )}
                  </dealer-listing-enhancement>
                );
              })}
            </div>

            {this.hasMoreDealers() && (
              <div class="text-center my-8 @md:max-w-sm @md:mx-auto">
                <ico-button buttonSize="large" buttonStyle="tertiary" label="See More Participating Dealers" whenClicked={this.onShowMoreDealersClick} />
              </div>
            )}

            {this.shouldDisplayBuyingCenters && (
              <div class="dealer-list dealer-list--buy-centers mt-4">
                <slot name="buying-centers" />
                {this.buyingCenters.map((dealerInfo, index) => {
                  return (
                    <dealer-listing-enhancement
                      id={`dealer-item-${this.allocatedDealers.length + index + 1}`}
                      dealerInfo={dealerInfo}
                      index={this.allocatedDealers.length + index}
                      isClosest={false}>
                      <offer-send
                        slot="right"
                        hasOffer={this.isOfferSentToDealer(dealerInfo.dealerId)}
                        whenClicked={() => {
                          this.onOfferSendClick(dealerInfo.dealerId);
                        }}
                      />
                      {this.isOfferSentToDealer(dealerInfo.dealerId) && dealerInfo.acceptAppointmentRequests && (
                        <offer-appointment
                          slot="right"
                          appointmentMonth={dealerInfo.appointmentMonth}
                          appointmentTime={dealerInfo.appointmentTime}
                          appointmentYear={dealerInfo.appointmentYear}
                          appointmentDay={dealerInfo.appointmentDay}
                          dayOfWeek={dealerInfo.dayOfWeek}
                          isRequestedAppointment={dealerInfo.isRequestedAppointment}
                          timeFrame={dealerInfo.timeFrame}
                          buttonStyle="secondary"
                          whenClicked={() => {
                            this.onDealerScheduleClick(dealerInfo.dealerId, index);
                          }}
                        />
                      )}
                    </dealer-listing-enhancement>
                  );
                })}
              </div>
            )}

            <slot name="footer" />
          </div>
        </div>
        <ico-modal displayX={true} id="appointment" callback={() => this.onModalClose()}>
          {this.renderModal()}
        </ico-modal>
      </Host>
    );
  }
}
